<template>
  <div class="pixel-canvas">
    <div class="canvas-grid">
      <div v-for="(row, rowIndex) in visibleRows" :key="rowIndex" class="pixel-row">
        <div
          v-for="(value, colIndex) in row"
          :key="colIndex"
          @click="emitCellClicked(rowIndex, colIndex, value)"
          @mouseenter="handlePixelHover(rowIndex, colIndex)"
          @mouseleave="handlePixelLeave"
          class="pixel-cell"
          :class="{
            'pixel-cell--active': isCellActive(value),
            'pixel-cell--hover': hoveredCell.row === rowIndex && hoveredCell.col === colIndex,
          }"
          :style="getCellStyle(value)"
        >
          <div class="pixel-overlay"></div>
        </div>
      </div>
    </div>

    <div v-if="hoveredCell.row !== -1" class="hover-tooltip">
      <div class="tooltip-title">Global Position</div>
      <div class="tooltip-coords">
        X: {{ formatNumber(getAbsoluteX(hoveredCell.col)) }} | Y:
        {{ formatNumber(getAbsoluteY(hoveredCell.row)) }}
      </div>
    </div>
  </div>
</template>

<script>
import { Environment } from '../environment.constant.js';

export default {
  name: 'BrickTable',
  props: {
    rows: { type: Array, required: true },
    posGlobalX: { type: Number, required: true },
    posGlobalY: { type: Number, required: true },
  },
  emits: ['cell-clicked', 'dimensions-changed'],
  data() {
    return {
      numberOfColumns: 0,
      numberOfRows: 0,
      hoveredCell: { row: -1, col: -1 },
      ro: null,
      roRafId: 0,
    };
  },
  computed: {
    visibleRows() {
      const cols = this.numberOfColumns > 0 ? this.numberOfColumns : Environment.FieldWidth;
      const rowCount = this.numberOfRows > 0 ? this.numberOfRows : Environment.FieldHeight;
      return (this.rows || [])
        .slice(0, rowCount)
        .map((r) => (Array.isArray(r) ? r.slice(0, cols) : r));
    },
  },
  watch: {
    numberOfColumns(val) {
      this.$emit('dimensions-changed', { cols: val, rows: this.numberOfRows });
    },
    numberOfRows(val) {
      this.$emit('dimensions-changed', { cols: this.numberOfColumns, rows: val });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.computeColumns();
      this.computeRows();
    });
    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(() => {
        if (this.roRafId) cancelAnimationFrame(this.roRafId);
        this.roRafId = requestAnimationFrame(() => {
          this.computeColumns();
          this.computeRows();
          this.roRafId = 0;
        });
      });
      this.ro.observe(this.$el);
    } else {
      window.addEventListener('resize', this._onResize);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this._onResize);
    if (this.ro) this.ro.disconnect();
    if (this.roRafId) cancelAnimationFrame(this.roRafId);
  },
  methods: {
    normalizeCell(value) {
      if (value && typeof value === 'object') {
        return {
          char: value.char || '',
          hasImage: !!value.hasImage,
          previewUrl: value.previewUrl || null,
          originalUrl: value.originalUrl || null,
        };
      }
      return { char: value || '', hasImage: false, previewUrl: null, originalUrl: null };
    },
    emitCellClicked(rowIndex, colIndex, value) {
      this.$emit('cell-clicked', { rowIndex, colIndex, cell: this.normalizeCell(value) });
    },
    handlePixelHover(rowIndex, colIndex) {
      this.hoveredCell = { row: rowIndex, col: colIndex };
    },
    handlePixelLeave() {
      this.hoveredCell = { row: -1, col: -1 };
    },
    isCellActive(value) {
      const cell = this.normalizeCell(value);
      return !!cell.char || cell.hasImage;
    },
    getCellStyle(value) {
      const cell = this.normalizeCell(value);
      if (cell.hasImage && cell.previewUrl) {
        return {
          backgroundImage: `url(${cell.previewUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: '#e2e8f0',
        };
      }
      return { backgroundColor: this.getPixelColor(cell.char) };
    },
    getPixelColor(charValue) {
      if (!charValue) return '#f8fafc';
      const colors = [
        '#667eea',
        '#764ba2',
        '#f093fb',
        '#f5576c',
        '#4facfe',
        '#00f2fe',
        '#43e97b',
        '#38f9d7',
        '#ffecd2',
        '#fcb69f',
        '#a8edea',
        '#fed6e3',
      ];
      const hash = charValue
        .toString()
        .split('')
        .reduce((a, b) => {
          a = (a << 5) - a + b.charCodeAt(0);
          return a & a;
        }, 0);
      return colors[Math.abs(hash) % colors.length];
    },
    getAbsoluteX(localCol) {
      return this.posGlobalX + localCol;
    },
    getAbsoluteY(localRow) {
      return this.posGlobalY + localRow;
    },
    formatNumber(num) {
      return num.toLocaleString();
    },
    _onResize() {
      this.computeColumns();
      this.computeRows();
    },
    computeColumns() {
      if (!this.$el) return;
      const rect = this.$el.getBoundingClientRect();
      // subtract canvas-grid left+right border (2px each = 4px)
      const next = Math.max(1, Math.floor((rect.width - 4) / 22));
      if (next !== this.numberOfColumns) {
        this.numberOfColumns = next;
      }
    },
    computeRows() {
      if (!this.$el) return;
      const rect = this.$el.getBoundingClientRect();
      // subtract canvas-grid top+bottom border (2px each = 4px)
      const next = Math.max(1, Math.floor((rect.height - 4) / 22));
      if (next !== this.numberOfRows) {
        this.numberOfRows = next;
      }
    },
  },
};
</script>

<style scoped>
.pixel-canvas {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  padding: 0;
}
.canvas-grid {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border: 2px solid rgba(102, 126, 234, 0.2);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  animation: none !important;
  transition: none !important;
}
.pixel-row {
  display: flex;
  flex: 1;
  min-height: 0;
}
.pixel-cell {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  border: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  position: relative;
  background: #f8fafc;
}
.pixel-cell:hover {
  z-index: 10;
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}
.pixel-cell--active {
  border-color: rgba(0, 0, 0, 0.1);
}
.pixel-cell--active:hover {
  filter: brightness(1.1);
}
.pixel-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.3) 0%,
    transparent 50%,
    rgba(0, 0, 0, 0.1) 100%
  );
  pointer-events: none;
  opacity: 0;
}
.pixel-cell:hover .pixel-overlay {
  opacity: 1;
}
.hover-tooltip {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-family: 'Courier New', monospace;
  font-weight: 500;
  pointer-events: none;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-width: 180px;
}
.tooltip-title {
  font-size: 0.75rem;
  opacity: 0.8;
  margin-bottom: 0.25rem;
  color: #a0a0a0;
}
.tooltip-coords {
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}
@media (max-width: 480px) {
  .pixel-cell:hover {
    transform: none;
  }
}
</style>
